import React from 'react';
import Accordion, { AccordionItem } from '../../../../core/components/Accordion';
import MonitorStatusIcon from '../components/MonitorStatusIcon';
import StaticTable, { StaticTableColumn } from '../../../../core/components/StaticTable';
import utilSvc from '../../../../core/utilSvc';
import { SystemStatEvent, SystemStatItem } from '../../data/datamonitorRepo';

type SystemSection = 'system' | 'integrity';

type SystemStatSectionProps = {
    variant: SystemSection;
    title?: string;
    rows: SystemStatItem[];
    onToggle: (section: SystemSection, id: string) => void;
    onDetail?: () => void;
};

const fmtDate = (d: Date | null): string => (d ? utilSvc.dt.toTextWithTime(d) : '');
const fmtVariance = (v: number | null): string => (v === null ? '' : v.toLocaleString());

const buildEventColumns = (isIntegrity: boolean): StaticTableColumn<SystemStatEvent>[] => {
    const cols: StaticTableColumn<SystemStatEvent>[] = [
        {
            key: 'status',
            header: 'Status',
            width: '60px',
            className: 'col-status',
            render: (ev): JSX.Element => <MonitorStatusIcon status={ev.Status} />,
        },
        { key: 'elapsed', header: 'Elapsed', render: (ev): string => ev.ElapsedTxt },
        { key: 'changed', header: 'Changed', render: (ev): string => fmtDate(ev.Changed) },
        {
            key: 'checked',
            header: isIntegrity ? 'Last Checked' : 'Last Activity',
            render: (ev): string => fmtDate(ev.LastChecked),
        },
    ];
    if (isIntegrity) {
        cols.push({ key: 'variance', header: 'Variance', render: (ev): string => fmtVariance(ev.Variance) });
    }
    cols.push({ key: 'details', header: 'Details', render: (ev): string => ev.Details });
    return cols;
};

const SystemStatSection = ({ variant, title, rows, onToggle, onDetail }: SystemStatSectionProps): JSX.Element => {
    const isIntegrity = variant === 'integrity';
    const eventColumns = buildEventColumns(isIntegrity);

    return (
        <div className={`datamonitor-system-section datamonitor-system-${variant}`}>
            {title ? <h5 className="datamonitor-system-header">{title}</h5> : null}

            <Accordion
                headerRow={
                    <>
                        <span className="col-name">{isIntegrity ? 'Test' : 'Process'}</span>
                        <span className="col-elapsed">Elapsed (hours:mins:sec)</span>
                        <span className="col-changed">Changed</span>
                        <span className="col-checked">{isIntegrity ? 'Last Checked' : 'Last Activity'}</span>
                        <span className="col-extra">{isIntegrity ? 'Variance' : 'Started'}</span>
                    </>
                }
            >
                {rows.length === 0 ? <div className="datamonitor-system-empty">No records</div> : null}

                {rows.map((row, index) => (
                    <AccordionItem
                        key={row.Id || `${row.MonitorType}-${index}`}
                        open={!!row.expand}
                        // rows without an Id can't be queried for events, so they're not expandable
                        expandable={!!row.Id}
                        onToggle={(): void => onToggle(variant, row.Id)}
                        header={
                            <>
                                <span className="col-name">
                                    <MonitorStatusIcon status={row.Status} />
                                    <span className="row-name-text">{row.name}</span>
                                </span>
                                <span className="col-elapsed">{row.ElapsedTxt}</span>
                                <span className="col-changed">{fmtDate(row.Changed)}</span>
                                <span className="col-checked">{fmtDate(row.LastChecked)}</span>
                                <span className="col-extra">
                                    {isIntegrity ? fmtVariance(row.Variance) : fmtDate(row.StartTime)}
                                </span>
                            </>
                        }
                    >
                        <div className="datamonitor-system-detail">
                            {isIntegrity && row.desc ? <div className="row-desc">{row.desc}</div> : null}
                            {isIntegrity && row.hasDetail && onDetail ? (
                                <button
                                    type="button"
                                    className="datamonitor-system-detail-link"
                                    onClick={onDetail}
                                >
                                    details...
                                </button>
                            ) : null}
                            <div className="row-records-count">{row.events.length} record(s)</div>
                            {row.events.length > 0 ? (
                                <StaticTable
                                    className="datamonitor-system-events"
                                    columns={eventColumns}
                                    rows={row.events}
                                    getRowKey={(_ev, i): number => i}
                                />
                            ) : null}
                        </div>
                    </AccordionItem>
                ))}
            </Accordion>
        </div>
    );
};

export default SystemStatSection;
